import type { GridColDef } from "@mui/x-data-grid-premium";
import { Stack, Typography } from "@mui/material";
import type { DocDataGrid_ItemFragment } from "../DocDataGrid.generated.js";
import type { DocDataGridRow } from "../types.js";
import type { ProductColumns__ItemFragment } from "./productColumns.generated.js";
import { ProductReplaceCreateButton } from "../cells/ProductReplaceCreateButton.js";
import type { ProductReplaceCreateButtonOnSelectFn } from "../cells/ProductReplaceCreateButton.js";
import type { useFisSolr } from "../../sap-s4-hana/hooks.js";
import type { ProductCreateModalProps } from "../../products/modals/ProductCreateModal.js";

interface Options {
  headerName: string;
  canEdit: boolean;
  showFisSolr: boolean;
  sapIntegrationSettings: ReturnType<
    typeof useFisSolr
  >["sapIntegrationSettings"];
  onProductSelect: (
    itemId: string,
    ...args: Parameters<ProductReplaceCreateButtonOnSelectFn>
  ) => ReturnType<ProductReplaceCreateButtonOnSelectFn>;
  onProductCreate: (
    itemId: string,
    values: Parameters<ProductCreateModalProps["handleSubmit"]>[0]
  ) => Promise<void> | void;
  projectId: string;
  docId: string;
}

export function createProductArticleNumberColumn<
  T extends DocDataGrid_ItemFragment & ProductColumns__ItemFragment,
>(options: Options): GridColDef<DocDataGridRow<T>> {
  return {
    field: "productArticleNumber",
    headerName: options.headerName,
    width: 160,
    editable: false,
    sortable: false,
    filterable: false,
    renderCell: params => {
      if (params.row.kind === "input" || !params.row.item) return null;
      const item = params.row.item;
      if (item.type !== "paid" || item.isItemEliminated) return null;
      const articleNumber = item.product?.articleNumber;
      const hasProduct = Boolean(item.product?.title || articleNumber);
      const itemId = item.id;
      return (
        <Stack
          direction="row"
          spacing={0}
          sx={{
            alignItems: "center",
            width: "100%",
          }}
        >
          <ProductReplaceCreateButton
            docId={options.docId}
            itemId={itemId}
            hasProduct={hasProduct}
            canEdit={options.canEdit}
            showFisSolr={options.showFisSolr}
            sapIntegrationSettings={options.sapIntegrationSettings}
            productSearchFilterDefinitions={null}
            onProductSelect={(product, filters) =>
              options.onProductSelect(itemId, product, filters)
            }
            onProductCreate={values => options.onProductCreate(itemId, values)}
          />
          <Typography variant="body2" noWrap>
            {articleNumber ?? "–"}
          </Typography>
        </Stack>
      );
    },
  };
}
